import React, { useRef, useState, useCallback } from 'react';

const THRESHOLD = 70;
const MAX_PULL  = 120;

export default function PullToRefresh({ onRefresh, children, className = '' }) {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing]    = useState(false);
  const touchStartY  = useRef(0);
  const pulling       = useRef(false);
  const containerRef  = useRef(null);

  const handleTouchStart = useCallback((e) => {
    if (refreshing) return;
    const scrollTop = containerRef.current?.scrollTop ?? 0;
    if (scrollTop <= 0) {
      touchStartY.current = e.touches[0].clientY;
      pulling.current = true;
    }
  }, [refreshing]);

  const handleTouchMove = useCallback((e) => {
    if (!pulling.current || refreshing) return;
    const diff = e.touches[0].clientY - touchStartY.current;
    if (diff > 0) {
      // Dampen the pull distance
      const dampened = Math.min(diff * 0.5, MAX_PULL);
      setPullDistance(dampened);
      if (dampened > 10) e.preventDefault();
    } else {
      pulling.current = false;
      setPullDistance(0);
    }
  }, [refreshing]);

  const handleTouchEnd = useCallback(async () => {
    if (!pulling.current) return;
    pulling.current = false;

    if (pullDistance >= THRESHOLD && onRefresh) {
      setRefreshing(true);
      setPullDistance(THRESHOLD);
      try {
        await onRefresh();
      } finally {
        setRefreshing(false);
        setPullDistance(0);
      }
    } else {
      setPullDistance(0);
    }
  }, [pullDistance, onRefresh]);

  const indicatorOpacity = Math.min(pullDistance / THRESHOLD, 1);
  const indicatorRotation = refreshing ? '' : `rotate(${(pullDistance / THRESHOLD) * 360}deg)`;

  return (
    <div
      ref={containerRef}
      className={className}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ position: 'relative' }}
    >
      {/* Pull indicator */}
      <div
        className="flex items-center justify-center overflow-hidden transition-all"
        style={{
          height: pullDistance > 0 || refreshing ? `${Math.max(pullDistance, refreshing ? 40 : 0)}px` : 0,
          opacity: indicatorOpacity,
        }}
      >
        <span
          className={`text-lg ${refreshing ? 'animate-spin' : ''}`}
          style={{ transform: indicatorRotation }}
        >
          ↻
        </span>
        <span className="text-xs text-gray-500 ml-2">
          {refreshing
            ? 'Refreshing…'
            : pullDistance >= THRESHOLD
            ? 'Release to refresh'
            : 'Pull down to refresh'}
        </span>
      </div>

      {children}
    </div>
  );
}
